import React, { useReducer, useRef } from 'react';
import { LocalDate, YearMonth, DateRange, LocalTime, ZonedTime, RangePreset, ComparePreset } from '../../model/datePicker/types';
import { PickerState, PickerTarget, pickerReducer, resolveB, resolveDisplayRange } from '../../model/datePicker/pickerState';
import { resolveCutoff } from '../../model/datePicker/resolveCutoff';
import { resolveContinuationStrips, ContinuationStrips } from '../../model/datePicker/continuationStrips';
import { daysBetween, shiftYearMonth } from '../../model/datePicker/dateHelpers';
import { CalendarGrid } from './CalendarGrid';
import { PanelHeader } from './PanelHeader';
import { ContinuationStrip } from './ContinuationStrip';
import { Sidebar } from './Sidebar';
import { Footer } from './Footer';
import { formatStripLabel } from './format';
import './panel.scss';

interface AppliedSelection {
  a: DateRange,
  b: DateRange,
  cutoff: LocalTime | null,
  rangePreset: RangePreset,
  comparePreset: ComparePreset
}

// Una columna = cabecera + grid + tiras opcionales. Derived y Custom son la
// misma forma repetida con props distintas — es la duplicación real la que
// justifica este sub-componente.
//
// Orden de DOM (tabulación) ≠ orden visual — §10: la tira, cuando está,
// entra en el orden de tabulación justo después del grid que describe,
// aunque la tira "before" se dibuje por encima. `order` de flexbox separa
// una cosa de la otra sin tocar el DOM.
const PanelColumn = ({ month, range, axis, slotLabel, strips, onDayClick, onDayHover, onNav, onSetMonth }: PanelColumnProps) => (
  <div className="rdp-panel-column">
    <PanelHeader month={month} slotLabel={slotLabel} onNav={onNav} onSetMonth={onSetMonth} />
    <CalendarGrid
      panelMonth={month}
      range={range}
      axis={axis}
      onDayClick={onDayClick}
      onDayHover={onDayHover}
      onNavigateMonth={onNav}
    />
    {
      strips.beforeMonth &&
        <ContinuationStrip
          kind="before"
          axis={axis}
          label={formatStripLabel('before', range.start, Number(month.slice(0, 4)))}
          onNavigate={() => onSetMonth(strips.beforeMonth as YearMonth)}
        />
    }
    {
      strips.afterMonth &&
        <ContinuationStrip
          kind="after"
          axis={axis}
          label={formatStripLabel('after', range.end, Number(month.slice(0, 4)))}
          onNavigate={() => onSetMonth(strips.afterMonth as YearMonth)}
        />
    }
  </div>
);

interface PanelColumnProps {
  month: YearMonth,
  range: DateRange,
  axis: 'A' | 'B',
  slotLabel?: { text: string, axis: 'A' | 'B' },
  strips: ContinuationStrips,
  onDayClick: (day: LocalDate) => void,
  onDayHover: (day: LocalDate | null) => void,
  onNav: (delta: number) => void,
  onSetMonth: (month: YearMonth) => void
}

// Chrome completo del picker: barra lateral + uno o dos calendarios + footer,
// cableado sobre pickerReducer. Mode=Derived / Mode=Custom es enteramente
// `comparePreset === 'custom'` — §01.
const DatePickerPanel = ({ initialState, today, now, onApply, onCancel }: Props) => {
  const openStateRef = useRef(initialState);
  const [ state, dispatch ] = useReducer(pickerReducer, initialState);

  const isCustom = state.comparePreset === 'custom';
  const rangeA = resolveDisplayRange(state, 'A');
  const rangeB = resolveDisplayRange(state, 'B');
  const b = resolveB(state);
  const days = daysBetween(state.a.start, state.a.end) + 1;
  const cutoff = resolveCutoff(state.a, today, now);

  const clickDay = (target: PickerTarget) => (day: LocalDate) => dispatch({ type: 'CLICK_DAY', target, day });
  const hoverDay = (_target: PickerTarget) => (day: LocalDate | null) => dispatch({ type: 'HOVER_DAY', day });
  const navMonth = (slot: 'left' | 'right') => (delta: number) => dispatch({ type: 'NAV_MONTH', slot, delta });
  const setMonth = (slot: 'left' | 'right') => (month: YearMonth) => dispatch({ type: 'SET_MONTH', slot, month });

  const handleApply = () => {
    dispatch({ type: 'APPLY' });
    onApply({ a: state.a, b, cutoff, rangePreset: state.rangePreset, comparePreset: state.comparePreset });
  };

  const handleCancel = () => {
    dispatch({ type: 'CANCEL', restoreTo: openStateRef.current });
    onCancel();
  };

  const leftMonth = state.panelMonth;
  const rightMonth = isCustom ? state.panelBMonth : shiftYearMonth(state.panelMonth, 1);

  // En Derived los dos paneles enlazados son una sola vista — el límite
  // interno entre ellos nunca lleva tira (§07). En Custom cada panel evalúa
  // contra su propio mes, y los dos pueden llevar tira a la vez.
  const stripsLeft = resolveContinuationStrips(rangeA, isCustom ? [ leftMonth ] : [ leftMonth, rightMonth ]);
  const stripsRight = isCustom
    ? resolveContinuationStrips(rangeB, [ rightMonth ])
    : { beforeMonth: null, afterMonth: null };

  return (
    <div className="rdp-panel">
      <div className="rdp-panel__body">
        <Sidebar selected={state.rangePreset} onSelect={preset => dispatch({ type: 'SELECT_RANGE_PRESET', preset, today })} />
        <div className="rdp-panel__calendars">
          <PanelColumn
            month={leftMonth}
            range={rangeA}
            axis="A"
            slotLabel={isCustom ? { text: 'Period A', axis: 'A' } : undefined}
            strips={stripsLeft}
            onDayClick={clickDay('A')}
            onDayHover={hoverDay('A')}
            onNav={navMonth('left')}
            onSetMonth={setMonth('left')}
          />
          <PanelColumn
            month={rightMonth}
            range={isCustom ? rangeB : rangeA}
            axis={isCustom ? 'B' : 'A'}
            slotLabel={isCustom ? { text: 'Period B', axis: 'B' } : undefined}
            strips={stripsRight}
            onDayClick={clickDay(isCustom ? 'B' : 'A')}
            onDayHover={hoverDay(isCustom ? 'B' : 'A')}
            onNav={navMonth('right')}
            onSetMonth={setMonth('right')}
          />
        </div>
      </div>
      <Footer
        comparePreset={state.comparePreset}
        onSelectCompare={preset => dispatch({ type: 'SELECT_COMPARE_PRESET', preset })}
        a={state.a}
        b={b}
        days={days}
        cutoff={cutoff}
        onCancel={handleCancel}
        onApply={handleApply}
      />
    </div>
  );
}

interface Props {
  initialState: PickerState,
  today: LocalDate,
  now: ZonedTime,
  onApply: (selection: AppliedSelection) => void,
  onCancel: () => void
}

export { DatePickerPanel, AppliedSelection };
